import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { PharmacyService } from '../../../../services/pharmacy/pharmacy.service';
import { PharmacyDTO, PharmacyVTO } from '../../../../services/pharmacy/pharmacy.model';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-edit-pharmacy-form',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './edit-pharmacy-form.html',
  styleUrl: './edit-pharmacy-form.scss',
})
export class EditPharmacyForm implements OnInit {
  private router = inject(Router);
  private pharmacyService = inject(PharmacyService);

  @Input() pharmacyData!: PharmacyVTO;

  formSubmitted = false;
  commercialRegistrationFile: File | null = null;
  taxRegistrationCertificateFile: File | null = null;
  pharmacyContractFile: File | null = null;
  pharmacyLicenseFile: File | null = null;
  pharmacistNationalIdFile: File | null = null;
  commercialRegistrationError: string | null = null;
  taxRegistrationCertificateError: string | null = null;
  pharmacyContractError: string | null = null;
  pharmacyLicenseError: string | null = null;
  pharmacistNationalIdError: string | null = null;
  submissionError: string | null = null;
  isDraggingCommercial = false;
  isDraggingTax = false;
  isDraggingContract = false;
  isDraggingLicense = false;
  isDraggingNationalId = false;
  loading = signal(false);

  requestForm = new FormGroup({
    nickname: new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(25)]),
    taxId: new FormControl('', [Validators.required, Validators.pattern(/^\d{9}$/)]),
    address: new FormControl('', [Validators.required, Validators.maxLength(2000)]),
  });

  ngOnInit() {
    if (this.pharmacyData) {
      this.requestForm.patchValue({
        nickname: this.pharmacyData.nickName,
        taxId: this.pharmacyData.taxIdentificationNumber,
        address: this.pharmacyData.address,
      });
    }
  }

  onFileSelect(event: Event, type: 'commercial' | 'tax' | 'contract' | 'license' | 'nationalId') {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0], type);
      input.value = '';
    }
  }

  onDragOver(event: DragEvent, type: 'commercial' | 'tax' | 'contract' | 'license' | 'nationalId') {
    event.preventDefault();
    event.stopPropagation();
    this.setDragState(type, true);
  }

  onDragLeave(event: DragEvent, type: 'commercial' | 'tax' | 'contract' | 'license' | 'nationalId') {
    event.preventDefault();
    event.stopPropagation();
    this.setDragState(type, false);
  }

  onDrop(event: DragEvent, type: 'commercial' | 'tax' | 'contract' | 'license' | 'nationalId') {
    event.preventDefault();
    event.stopPropagation();
    this.setDragState(type, false);
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0], type);
    }
  }

  private setDragState(type: 'commercial' | 'tax' | 'contract' | 'license' | 'nationalId', state: boolean) {
    switch (type) {
      case 'commercial':
        this.isDraggingCommercial = state;
        break;
      case 'tax':
        this.isDraggingTax = state;
        break;
      case 'contract':
        this.isDraggingContract = state;
        break;
      case 'license':
        this.isDraggingLicense = state;
        break;
      case 'nationalId':
        this.isDraggingNationalId = state;
        break;
    }
  }

  private handleFile(file: File, type: 'commercial' | 'tax' | 'contract' | 'license' | 'nationalId') {
    this.setError(type, null);
    if (file.size > 5 * 1024 * 1024) {
      this.setError(type, `File ${file.name} exceeds 5MB limit`);
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      this.setError(type, 'Only JPG, PNG, and PDF files are allowed');
      return;
    }
    switch (type) {
      case 'commercial':
        this.commercialRegistrationFile = file;
        break;
      case 'tax':
        this.taxRegistrationCertificateFile = file;
        break;
      case 'contract':
        this.pharmacyContractFile = file;
        break;
      case 'license':
        this.pharmacyLicenseFile = file;
        break;
      case 'nationalId':
        this.pharmacistNationalIdFile = file;
        break;
    }
  }

  removeFile(type: 'commercial' | 'tax' | 'contract' | 'license' | 'nationalId') {
    this.setError(type, null);
    switch (type) {
      case 'commercial':
        this.commercialRegistrationFile = null;
        break;
      case 'tax':
        this.taxRegistrationCertificateFile = null;
        break;
      case 'contract':
        this.pharmacyContractFile = null;
        break;
      case 'license':
        this.pharmacyLicenseFile = null;
        break;
      case 'nationalId':
        this.pharmacistNationalIdFile = null;
        break;
    }
  }

  private setError(type: 'commercial' | 'tax' | 'contract' | 'license' | 'nationalId', error: string | null) {
    switch (type) {
      case 'commercial':
        this.commercialRegistrationError = error;
        break;
      case 'tax':
        this.taxRegistrationCertificateError = error;
        break;
      case 'contract':
        this.pharmacyContractError = error;
        break;
      case 'license':
        this.pharmacyLicenseError = error;
        break;
      case 'nationalId':
        this.pharmacistNationalIdError = error;
        break;
    }
  }

  onSubmit() {
    this.formSubmitted = true;
    this.submissionError = null;
    if (this.requestForm.invalid) {
      return;
    }
    this.loading.set(true);
    const formValue = this.requestForm.value;
    // const pharmacyData: PharmacyDTO = {
    //   nickName: formValue.nickname!,
    //   taxIdentificationNumber: formValue.taxId!,
    //   address: formValue.address!,
    //   pharmacistNationalIdFid: this.pharmacyData.pharmacistNationalIdFid || '917223989239698944',
    //   commercialRegistrationFid: this.pharmacyData.commercialRegistrationFid || '917223989239698944',
    //   taxRegistrationCertificateFid: this.pharmacyData.taxRegistrationCertificateFid || '917223989239698944',
    //   pharmacyContractFid: this.pharmacyData.pharmacyContractFid || '917223989239698944',
    //   pharmacyLicenseFid: this.pharmacyData.pharmacyLicenseFid || '917223989239698944',
    // };

    // this.pharmacyService
    //   .updatePharmacy(this.pharmacyData.id, pharmacyData)
    //   .pipe(finalize(() => this.loading.set(false)))
    //   .subscribe({
    //     next: () => {
    //       this.router.navigate(['user/pharmacies']);
    //     },
    //     error: (error) => {
    //       console.error('Error updating pharmacy:', error);
    //       this.submissionError = error.messageEn || 'An error occurred while updating the pharmacy.';
    //     },
    //   });
  }

  onCancel() {
    this.router.navigate(['user/pharmacies']);
  }
}
